/**
 * <brick-announcement>
 * Rotates announcement messages (prev/next + optional autoplay) and handles
 * an optional dismiss stored for the browser session.
 * Autoplay pauses on hover/focus, when the tab is hidden and is disabled
 * entirely for prefers-reduced-motion.
 */
(() => {
  if (customElements.get('brick-announcement')) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class BrickAnnouncement extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.querySelectorAll('[data-announcement-message]'));
      this.index = Math.max(0, this.messages.findIndex((m) => m.classList.contains('is-active')));
      this.storageKey = `brick-announcement:${this.dataset.sectionId}`;

      if (this.hasAttribute('data-dismissible')) {
        try {
          if (sessionStorage.getItem(this.storageKey) === '1' && !window.Shopify?.designMode) {
            this.hidden = true;
            return;
          }
        } catch (e) {
          /* storage unavailable: keep visible */
        }
        this.querySelector('[data-announcement-dismiss]')?.addEventListener('click', () => this.dismiss());
      }

      if (this.messages.length < 2) return;

      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.go(this.index + 1, true));

      this.delay = parseInt(this.dataset.autoplay, 10) || 0;
      if (this.delay && !reducedMotion()) {
        this.pause = () => this.stop();
        this.resume = () => this.start();
        this.addEventListener('mouseenter', this.pause);
        this.addEventListener('mouseleave', this.resume);
        this.addEventListener('focusin', this.pause);
        this.addEventListener('focusout', this.resume);
        this.onVisibility = () => (document.hidden ? this.stop() : this.start());
        document.addEventListener('visibilitychange', this.onVisibility);
        this.start();
      }
    }

    disconnectedCallback() {
      this.stop();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.go(this.index + 1, false), this.delay);
    }

    stop() {
      window.clearInterval(this.timer);
    }

    go(next, userInitiated) {
      const count = this.messages.length;
      this.index = (next + count) % count;
      const track = this.querySelector('.announcement__track');
      // Announce only user-initiated changes to screen readers.
      if (track) track.setAttribute('aria-live', userInitiated ? 'polite' : 'off');

      this.messages.forEach((message, i) => {
        const active = i === this.index;
        message.classList.toggle('is-active', active);
        if (active) message.removeAttribute('aria-hidden');
        else message.setAttribute('aria-hidden', 'true');
        message.querySelectorAll('a').forEach((link) => {
          if (active) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        });
      });
    }

    dismiss() {
      try {
        sessionStorage.setItem(this.storageKey, '1');
      } catch (e) {
        /* ignore */
      }
      this.stop();
      this.hidden = true;
      document.dispatchEvent(new CustomEvent('brick:layout:change'));
    }
  }

  customElements.define('brick-announcement', BrickAnnouncement);
})();
